import { useScreenSize } from '@/themes/media-query'
import { Suspense, useCallback, useRef, useState } from 'react'
import { Await, useLoaderData } from 'react-router-typesafe'
import { CellPreparedEvent, ExportingEvent } from 'devextreme/ui/data_grid'
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Scrolling,
  StateStoring,
  DataGridRef,
  FilterRow,
  FilterPanel,
  Pager,
} from 'devextreme-react/data-grid'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { Workbook } from 'exceljs'
import { gridTotalItemsLabel } from '@/constants'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import DocumentHead from '@/components/document-head/DocumentHead'
import {
  getIrregularStampings,
  stampingAnomaliesPageRouteLoader,
} from '@/routes/hours/anomalies/stampings/StampingAnomaliesPage.route'
import { getPreviousWorkWeek } from '@/routes/hours/anomalies/anomalies.utils'
import DateColumn from '@/components/date-column/DateColumn'
import { GridCellColor } from '@/enums'
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar'
import { Button, DateRangeBox } from 'devextreme-react'
import { DateRangeBoxRef, DateRangeBoxTypes } from 'devextreme-react/cjs/date-range-box'
import { ClickEvent } from 'devextreme/ui/button'
import notify from 'devextreme/ui/notify'

const StampingAnomaliesPage = () => {
  const { minDate, maxDate, getIrregularsStamping } = useLoaderData<typeof stampingAnomaliesPageRouteLoader>()
  const refDateRangeBox = useRef<DateRangeBoxRef>(null)
  const [irregularsStamping, setIrregularStamping] = useState<any[]>([])
  const initialValue: [Date, Date] = [minDate, maxDate]
  const currentScreenSize = useScreenSize()
  const getGridHeight = useCallback(() => {
    let h = '79vh'
    if (currentScreenSize.isMedium) {
      h = '75vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '75vh'
    }
    return h
  }, [currentScreenSize])
  const gridRef = useRef<DataGridRef>(null)

  const onExporting = (e: ExportingEvent) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('IrregularsStamping')
    const component = e.component
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'IrregularsStamping.xlsx')
      })
    })
  }

  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === 'data' && e.column.dataField === 'type' && e.value) {
      switch (e.value) {
        case 'LATE': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`
          break
        }
        case 'MISSING': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.EMERALD}`

          break
        }
        case 'ENFORCED': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`
          break
        }
      }
    }
  }

  return (
    <>
      <Suspense fallback={<LoadingPanel />}>
        <Await resolve={getIrregularsStamping}>
          {(values) => (
            <>
              <div>
                <Toolbar>
                  <ToolbarItem location="before" locateInMenu="never">
                    <DateRangeBox
                      ref={refDateRangeBox}
                      elementAttr={{ class: 'irregular-daterange' }}
                      width={currentScreenSize.isLarge || currentScreenSize.isMedium ? '350px' : '250px'}
                      defaultValue={initialValue}
                      startDateLabel="Data Inizio"
                      endDateLabel="Data Fine"
                      applyValueMode="useButtons"
                      buttons={[
                        // 'dropDown' mantiene il calendario di default per la selezione manuale del range.
                        'clear',
                        'dropDown',
                        // Button custom: preseleziona la settimana lavorativa precedente (lun-ven).
                        {
                          name: 'previousWeek',
                          location: 'after',
                          options: {
                            icon: 'event',
                            hint: 'Imposta la settimana lavorativa precedente',
                            stylingMode: 'text',
                            onClick: () => {
                              const [start, end] = getPreviousWorkWeek(new Date())
                              refDateRangeBox.current?.instance().option('value', [start, end])
                            },
                          },
                        },
                      ]}
                      onValueChanged={(e: DateRangeBoxTypes.ValueChangedEvent) => {
                        if (e.value[1] === null)
                          refDateRangeBox.current?.instance().option('value', [e.value[0], e.value[0]])
                      }}
                    />
                  </ToolbarItem>
                  <ToolbarItem
                    location="after"
                    locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
                    showText="inMenu"
                  >
                    <Button
                      text="Reset"
                      icon="undo"
                      useSubmitBehavior={false}
                      width={'150px'}
                      onClick={(e: ClickEvent) => {
                        refDateRangeBox.current?.instance().option('value', initialValue)
                        setIrregularStamping(values.data)
                      }}
                    />
                  </ToolbarItem>
                  <ToolbarItem
                    location="after"
                    locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
                    showText="inMenu"
                  >
                    <Button
                      text="Ricarica"
                      icon="refresh"
                      useSubmitBehavior={false}
                      width={'150px'}
                      onClick={(e: ClickEvent) => {
                        gridRef?.current?.instance().beginCustomLoading('Recupero anomalie in corso....')
                        const values: Date[] = refDateRangeBox.current?.instance().option().value
                        getIrregularStampings(values[0], values[1])
                          .then((response) => {
                            setIrregularStamping(response.data)
                          })
                          .catch((error) => {
                            notify(`Errore recupero anomalie accessi. Dettagli: ${error}`, 'error', 3000)
                          })
                          .finally(() => {
                            gridRef?.current?.instance().endCustomLoading()
                          })
                      }}
                    />
                  </ToolbarItem>
                </Toolbar>
                <DataGrid
                  id={'irregular-stamping-datagrid'}
                  height={getGridHeight()}
                  dataSource={irregularsStamping.length > 0 ? irregularsStamping : values.data}
                  noDataText="Nessuna anomalia trovata"
                  className={'dx-card wide-card'}
                  showBorders={false}
                  showColumnLines={true}
                  wordWrapEnabled={false}
                  showRowLines={true}
                  rowAlternationEnabled={true}
                  allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
                  allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
                  width="100%"
                  onExporting={onExporting}
                  ref={gridRef}
                  columnHidingEnabled={
                    currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium
                  }
                  onCellPrepared={onCellPrepared}
                  remoteOperations={{
                    filtering: true,
                    grouping: true,
                    groupPaging: true,
                    paging: true,
                    sorting: true,
                    summary: true,
                  }}
                  grouping={{
                    // Fondamentale per il raggruppamento veloce!!!
                    autoExpandAll: true,
                  }}
                  groupPanel={{
                    visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
                    emptyPanelText: 'Trascina qui una colonna per raggruppare',
                  }}
                >
                  <FilterRow visible={true} />
                  <FilterPanel visible={true} />
                  <HeaderFilter visible={true} />
                  <Export enabled={true} />
                  <Scrolling mode="virtual" />
                  <Pager
                    visible={true}
                    allowedPageSizes={'auto'}
                    displayMode={'compact'}
                    showPageSizeSelector={false}
                    showInfo={true}
                    showNavigationButtons={false}
                    infoText={gridTotalItemsLabel}
                  />
                  <StateStoring
                    enabled={true}
                    type="localStorage"
                    storageKey={'irregular-stamping-datagrid'}
                    savingTimeout={50}
                  />
                  <Column dataField={'user.id'} caption="ID_IMPIEGATO" dataType="number" visible={false}></Column>
                  <Column dataField={'user.email'} caption="EMAIL" dataType="string"></Column>
                  <Column dataField={'user.fullName'} caption="DIPENDENTE" dataType="string"></Column>
                  <Column dataField="day" caption="DATA" dataType="date" allowHeaderFiltering={false}></Column>
                  <Column
                    dataField={'issue'}
                    caption="ANOMALIA"
                    dataType="string"
                    allowHeaderFiltering={false}
                  ></Column>
                  <Column dataField={'type'} caption="TIPOLOGIA" dataType="string"></Column>
                </DataGrid>
              </div>
            </>
          )}
        </Await>
      </Suspense>
    </>
  )
}

export default StampingAnomaliesPage
